import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Table } from "antd";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import AddAdditionModal from "../components/AddAdditionModal";

const SalaryHeadsList = () => {
  const [salaryHeads, setSalaryHeads] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isFullScreen, setFullScreen] = useState(false);
  const [selectedSalaryHead, setSelectedSalaryHead] = useState(null);

  const maximizeBtnRef = useRef(null);
  const isFetching = useRef(false); // Prevent multiple API calls

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  const [categoryFilter, setCategoryFilter] = useState("All");


  

  // Fetch salary heads
  const fetchSalaryHeads = async () => {
    if (isFetching.current) return; // Prevent concurrent calls
    isFetching.current = true;

    try {
      const response = await axios.get(`${apiUrl}/payroll/salary-heads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSalaryHeads(response.data);
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to fetch salary heads. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  };

  useEffect(() => {
    fetchSalaryHeads();
  }, []); // Run only once on mount

  const [selectedCategory, setSelectedCategory] = useState("");

  // Handle category filter
  const handleCategoryFilterChange = (value) => {
    setCategoryFilter(value);
  };
  

    // Filter Salary Heads Based on Selected Category
    const filteredSalaryHeads =
    categoryFilter === "All"
      ? salaryHeads
      : salaryHeads.filter((head) => head.category === categoryFilter);
  
  // Fullscreen toggle
  useEffect(() => {
    const handleClick = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        setFullScreen(true);
      } else {
        document.exitFullscreen();
        setFullScreen(false);
      }
    };

    const maximizeBtn = maximizeBtnRef.current;
    if (maximizeBtn) maximizeBtn.addEventListener("click", handleClick);

    return () => {
      if (maximizeBtn) maximizeBtn.removeEventListener("click", handleClick);
    };
  }, []);

  const handleAddClick = () => {
    setSelectedSalaryHead(null);
    setIsEditing(false);
  };

  const handleSave = (response) => {

    const salaryHead = {
      id: response.data.id,
      name: response.data.name,
      category: response.data.category,
      income_type: response.data.income_type,
      calculation_type: response.data.calculation_type,
      affects_net_salary: response.data.affects_net_salary,
      unit_amount: response.data.unit_amount,
      taxable: response.data.taxable,
      gratuity_applicable: response.data.gratuity_applicable,
      bonus_applicable: response.data.bonus_applicable,
      formula: response.data.formula,
      frequency: response.data.frequency,
      description: response.data.description,
      isdefault: response.data.isdefault,
      status: response.data.status,
      created_at: response.data.created_at,
      updated_at: response.data.updated_at,
    };
  
    setSalaryHeads((prevSalaryHeads) =>
      isEditing
        ? prevSalaryHeads.map((s) => (String(s.id) === String(salaryHead.id) ? salaryHead : s))
        : [salaryHead, ...prevSalaryHeads]
    );
    
  };
  

  const handleEditClick = (salaryHead) => {
    setSelectedSalaryHead(salaryHead);
    setIsEditing(true);
  };

  const handleDeleteClick = async (salaryHeadId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this salary head!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${apiUrl}/payroll/salary-head/${salaryHeadId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSalaryHeads((prevSalaryHeads) =>
          prevSalaryHeads.filter((salaryHead) => salaryHead.id !== salaryHeadId)
        );
        toast.success("Salary head has been deleted.");
      } catch (error) {
        const errorMessage = error.response?.data?.error || "Failed to delete salary head. Please try again.";
        Swal.fire("Error!", errorMessage, "error");
      }
    }
  };

  const handleCloseModal = () => {
    setSelectedSalaryHead(null);
    setIsEditing(false);
  };


  const handleStatusChange = async (record) => {
    const isActive = record.status === 1;
    const result = await Swal.fire({
      title: `Are you sure you want to ${isActive ? 'deactivate' : 'activate'} this salary head?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    });
  
    if (result.isConfirmed) {
      try {
        const updatedStatus = isActive ? 0 : 1;
        await axios.put(`${apiUrl}/payroll/salary-head/${record.id}`, { status: updatedStatus }, {
          headers: { Authorization: `Bearer ${token}` },
        });
  

        
        setSalaryHeads((prev) =>
          prev.map((head) =>
            head.id === record.id ? { ...head, status: updatedStatus } : head
          )
        );
        toast.success("Salary head status updated successfully.");
      } catch (error) {
        toast.error("Failed to update salary head status.");
      }
    }
  };
  

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name, record) => {
        let bgColor = "";
        let initial = "";
        switch (record.category) {
          case "Earnings":
            bgColor = "#99df99"; // Light green
            initial = "E";
            break;
          case "Deductions":
            bgColor = "#ffe6e6"; // Light red
            initial = "D";
            break;
          case "Reimbursement":
            bgColor = "#e6f7ff"; // Light blue
            initial = "R";
            break;
          default:
            bgColor = "#f0f0f0"; // Default gray
            initial = "?"; // For unknown category
        }
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {/* Small colored box */}
            <span
              style={{
                backgroundColor: bgColor,
                fontWeight: "bold",
                padding: "5px 10px",
                borderRadius: "5%",
                display: "inline-block",
                textAlign: "center",
                width: "30px",
                height: "30px",
              }}
            >
              {initial}
            </span>
            {/* Name */}
            
            <Link onClick={() => handleEditClick(record)} className="dropdown-item" to="#" data-bs-toggle="modal" data-bs-target="#add_salary_head">
               <span>{name}</span>
            </Link>
          </div>
        );
      },
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    
    { title: "Income Type", dataIndex: "income_type", key: "income_type", sorter: (a, b) => a.income_type.localeCompare(b.income_type) },
    { title: "Calculation Type", dataIndex: "calculation_type", key: "calculation_type", sorter: (a, b) => a.calculation_type.localeCompare(b.calculation_type) },
    { title: "Unit Amount", dataIndex: "unit_amount", key: "unit_amount", render: (amount) => (amount ? `$${amount}` : "-"), sorter: (a, b) => parseFloat(a.unit_amount) - parseFloat(b.unit_amount) },
    { title: "Affects Net Salary", dataIndex: "affects_net_salary", key: "affects_net_salary", render: (val) => (val ? "Yes" : "No"), sorter: (a, b) => a.affects_net_salary - b.affects_net_salary },
    { title: "Taxable", dataIndex: "taxable", key: "taxable", render: (val) => (val ? "Yes" : "No"), sorter: (a, b) => a.taxable - b.taxable },
    { title: "Gratuity Applicable", dataIndex: "gratuity_applicable", key: "gratuity_applicable", render: (val) => (val ? "Yes" : "No"), sorter: (a, b) => a.gratuity_applicable - b.gratuity_applicable },
    { title: "Bonus Applicable", dataIndex: "bonus_applicable", key: "bonus_applicable", render: (val) => (val ? "Yes" : "No"), sorter: (a, b) => a.bonus_applicable - b.bonus_applicable },
    { title: "Frequency", dataIndex: "frequency", key: "frequency", sorter: (a, b) => a.frequency.localeCompare(b.frequency) },
   
    {
      title: "Status",
      key: "status",
      render: (_, record) => {
        const isActive = record.status === 1;
        const statusText = isActive ? "Active" : "Inactive";
    
        return (
          <div className="dropdown action-label">
            <Link
              className={`btn btn-white btn-sm btn-rounded dropdown-toggle ${isActive ? 'text-success' : 'text-danger'}`}
              to="#"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className={`far fa-dot-circle ${isActive ? 'text-success' : 'text-danger'}`} />{' '}
              {statusText}
            </Link>
            <div className="dropdown-menu">
              <Link
                className="dropdown-item"
                to="#"
                onClick={() => handleStatusChange(record)}
              >
                <i className={`far fa-dot-circle ${!isActive ? 'text-success' : 'text-danger'}`} />{' '}
                {!isActive ? 'Activate' : 'Deactivate'}
              </Link>
            </div>
          </div>
        );
      },
    },
    
   
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div className="dropdown dropdown-action text-center">
          <Link to="#" className="action-icon dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
            <i className="material-icons">more_vert</i>
          </Link>
          <div className="dropdown-menu dropdown-menu-center">
            <Link onClick={() => handleEditClick(record)} className="dropdown-item" to="#" data-bs-toggle="modal" data-bs-target="#add_salary_head">
              <i className="fa fa-pencil m-r-5" /> Edit
            </Link>
            <button
              className="dropdown-item"
              disabled={record.isdefault === 1}
              onClick={() => handleDeleteClick(record.id)}
            >
              <i className="fa fa-trash m-r-5" /> Delete
            </button>
          </div>
        </div>
      ),
    },
    
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header page-top-icons">
          <div className="row align-items-center">
            <div className="col-md-4">
              <h3 className="page-title">Salary Heads</h3>
            </div>
            <div className="col-md-8 float-end ms-auto">
              <div className="d-flex title-head">
                <div className="view-icons">
                  
                  <Link to="#" className="grid-view btn btn-link" onClick={fetchSalaryHeads}>
                    <i className="las la-redo-alt" />
                  </Link>
                  <Link to="#" className="list-view btn btn-link" id="collapse-header" ref={maximizeBtnRef}>
                    <i className="las la-expand-arrows-alt" />
                  </Link>
                </div>
                <Link to="#" className="btn add-btn" data-bs-toggle="modal" data-bs-target="#add_salary_head" onClick={handleAddClick}>
                  <i className="la la-plus-circle" /> Add New
                </Link>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12">
            {loading ? (
              <div id="loader-wrapper-">
                <div className="loader">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <div className="d-flex justify-content-between align-items-center mb-3">
                <select
                  className="form-select w-auto"
                  value={categoryFilter}
                  onChange={(e) => handleCategoryFilterChange(e.target.value)}
                >
                  <option value="All">All Categories</option>
                  <option value="Earnings">Earnings</option>
                  <option value="Deductions">Deductions</option>
                  <option value="Reimbursements">Reimbursements</option>
                </select>
            </div>

            <Table 
  className="table-striped"   
  rowClassName={(record) => {
    switch (record.category) {
      case "Earnings":
        return "bg-light-green"; // Correct category name
      case "Deduction":
        return "bg-light-red";   // Correct category name
      case "Reimbursement":
        return "bg-light-blue";  // Correct category name
      default:
        return "";
    }
  }} 
  style={{ overflowX: "auto" }} 
  columns={columns.map((col, index) => {
    if (index === 0) {
      return { ...col, fixed: 'left', width: 100 }; // Freeze first column
    }
    if (index === 1) {
      return { ...col, fixed: 'left', width: 100 }; // Freeze second column
    }
    return { ...col, width: 110 }; // Set a uniform width for remaining columns
  })} 
  dataSource={filteredSalaryHeads} 
  rowKey={(record) => record.id} 
  scroll={{
    x: 3200, // Ensure this matches or exceeds total table width
    y: 300,  // Optional: Enable vertical scrolling
  }}
/>


                        </div>
            )}
          </div>
        </div>
      </div>
      <AddAdditionModal onAdd={handleSave} onClose={handleCloseModal} record={selectedSalaryHead} />
    </div>
  );
};

export default SalaryHeadsList;